import { type QuestionsResponse } from "@/api/questions";
import { type ReviewsResponse } from "@/api/reviews";
import { useFunctionsGatsby } from "@hooks/useFunctionsGatsby";
import { useReviewContext } from "@hooks/useReviewSnippet";
import { useTemplate } from "@hooks/useTemplate";
import { type ComponentProps } from "@ts/components";
import { type TemplateProduct } from "@ts/sanity";
import React, {
  ChangeEvent,
  KeyboardEventHandler,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FC,
} from "react";

type FitFeedbackStatisticProps = {
  id: number;
  rangeOptions: string[];
  title: string;
  averageRating: string;
};

export type ReviewsInputProps = ComponentProps & {
  gender: "Mens" | "Womens";
  productGroup: string;
  productHandle?: string;
  sku: string;
  siblingColours?: string[];
  reviewsSummary?: React.ReactNode;
};

export type ReviewsOutputProps = Omit<
  ReviewsInputProps,
  "colours" | "productHandle"
> &
  Partial<TemplateProduct> & {
    averageScore: number;
    fitFeedbackStatistic: FitFeedbackStatisticProps[];
    handleClearReviewFilter: () => void;
    handleClickReviewFilter: (filter: number) => void;
    handleLoadMoreQuestions: (event: { selected: number }) => void;
    handleLoadMoreReviews: (event: { selected: number }) => void;
    handleQuestionsSortOptionChange: ({
      target: { value },
    }: ChangeEvent<HTMLSelectElement>) => void;
    handleReviewsSortOptionChange: ({
      target: { value },
    }: ChangeEvent<HTMLSelectElement>) => void;
    handleSearch: (e: KeyboardEventHandler<HTMLInputElement>) => void;
    handleSearchIconClick: () => void;
    handleTabChange: (tab?: string) => void;
    loading: boolean;
    productUrl: string;
    questions: QuestionsResponse[];
    questionsSortOptions: {
      value: string;
      label: string;
    }[];
    questionsPerPage: number;
    reviewFilter: number;
    reviews: ReviewsResponse[];
    reviewsPerPage: number;
    reviewsSortOptions: {
      value: string;
      label: string;
    }[];
    starDistribution: {
      [key: number]: number;
    };
    totalQuestions: number;
    totalReviews: number;
    totalPageCount: number;
    totalQuestionPageCount: number;
    totalResultsCount: number;
  };

export const withReviews = (Component: FC<ReviewsOutputProps>) =>
  memo(
    ({
      name = "Reviews",
      gender,
      productGroup,
      productHandle,
      sku,
      siblingColours,
      reviewsSummary,
    }: ReviewsInputProps) => {
      const { productReviews, productQuestions, loading, errors } =
        useFunctionsGatsby();
      const { addProductToQueue, setTotalReviewsCount } = useReviewContext();
      const {
        product: {
          additionalReviewsLoadingMoreText,
          settingReviewInitalQuantity,
        },
      } = useTemplate();

      const size = settingReviewInitalQuantity || 6;
      const productUrl = `/products/${productHandle}`;
      const reviewsSortOptions = [
        {
          value: "highest_rating",
          label: "Highest Rating",
        },
        {
          value: "lowest_rating",
          label: "Lowest Rating",
        },
        {
          value: "date_desc",
          label: "Most Recent",
        },
      ];
      const questionsSortOptions = [
        {
          value: "date_desc",
          label: "Most Recent",
        },
        {
          value: "date_asc",
          label: "Oldest",
        },
      ];

      const [page, setPage] = useState<number>(1);
      const [reviewFilter, setReviewFilter] = useState<number>(0);
      const [average, setAverage] = useState<number>(0);
      const [starDistribution, setStarDistribution] = useState<
        Record<number, number>
      >({});
      const [fitFeedbackStatistic, setFitFeedbackStatistic] = useState<
        FitFeedbackStatisticProps[]
      >([]);

      const [reviews, setReviews] = useState<ReviewsResponse[]>([]);
      const [reviewsSortValue, setReviewsSortValue] = useState<string>(
        reviewsSortOptions[0].value,
      );
      const [questionsSortValue, setQuestionsSortValue] = useState<string>(
        questionsSortOptions[0].value,
      );
      const [searchKeyword, setSearchKeyword] = useState<string | undefined>(
        undefined,
      );
      const [total, setTotal] = useState<number>(0);
      const [questions, setQuestions] = useState<QuestionsResponse[]>([]);
      const [totalQuestions, setTotalQuestions] = useState<number>(0);
      const [questionPageCursor, setQuestionPageCursor] = useState<number>(1);
      const [totalPageCount, setTotalPageCount] = useState<number>(0);
      const [totalQuestionPageCount, setTotalQuestionPageCount] =
        useState<number>(0);
      const [totalResultsCount, setTotalResultsCount] = useState<number>(0);
      const [reviewsPerPage, setReviewsPerPage] = useState<number>(0);
      const [questionsPerPage, setQuestionsPerPage] = useState<number>(0);

      const mapStarDistribution = (ratings: Record<string, number>) => {
        return Array.from({ length: 5 })
          .map((_val, index) => 5 - index)
          .reduce((prev, curr) => {
            const buckets = Object.keys(ratings)?.filter(
              (key) => Number(key) === curr,
            );

            return {
              ...prev,
              [curr]: ratings[Number(buckets.toString())] || 0,
            };
          }, {});
      };

      const getReviews = useCallback(
        async (
          page: number,
          reviewFilter: number,
          sortOrder: string,
          keyword?: string,
        ) => {
          const { status, body } = await productReviews(
            productGroup,
            page,
            size,
            reviewFilter,
            sortOrder,
            keyword,
          );

          if (status !== "success") {
            return;
          }

          const {
            reviews: data,
            reviewCount,
            resultsCount,
            stats: { attributes, ratings },
            averageRating,
          } = body;

          const fitFeedbackStats: FitFeedbackStatisticProps[] = attributes.map(
            (attribute) => {
              return {
                id: attribute.id,
                rangeOptions: attribute.options,
                title: attribute.question,
                averageRating: attribute.average_rating,
              };
            },
          );

          // Checks if the next pagination arrow is disabled. If it is that is the last page
          const isLastPage =
            document.querySelector('[rel="next"]')?.ariaDisabled === "true";
          if (isLastPage) {
            setReviewsPerPage(resultsCount);
          } else {
            setReviewsPerPage(page * data.length);
          }

          if (!reviewFilter) {
            setStarDistribution(mapStarDistribution(ratings));
          }

          setAverage(parseFloat(averageRating));
          setReviews(data);
          setTotal(reviewCount);
          setTotalResultsCount(resultsCount);
          setTotalPageCount(Math.ceil(resultsCount / size));
          setFitFeedbackStatistic(fitFeedbackStats);

          // Set the reviews count in the context for use in ReviewNugget
          if (setTotalReviewsCount) {
            setTotalReviewsCount(reviewCount);
          }
        },
        [],
      );

      const getQuestions = useCallback(
        async (page: number, sortOrder: string) => {
          const { status, body } = await productQuestions(
            productGroup,
            page,
            5,
            sortOrder,
          );

          if (status !== "success") {
            return;
          }

          const { questions: data, questionsCount } = body;

          // Checks if the next pagination arrow is disabled. If it is that is the last page
          const isLastPage =
            document.querySelector('[rel="next"]')?.ariaDisabled === "true";
          if (isLastPage) {
            setQuestionsPerPage(questionsCount);
          } else {
            setQuestionsPerPage(page * data.length);
          }

          setQuestions(data);
          setTotalQuestions(questionsCount);
          setTotalQuestionPageCount(Math.ceil(questionsCount / 5));
        },
        [questions],
      );

      useEffect(() => {
        getReviews(page, reviewFilter, reviewsSortValue, searchKeyword);
      }, [page, reviewFilter, reviewsSortValue, searchKeyword]);

      useEffect(() => {
        getQuestions(questionPageCursor, questionsSortValue);
      }, [questionPageCursor, questionsSortValue]);

      const handleLoadMoreReviews = (event: { selected: number }) => {
        setPage(event.selected + 1);

        const element = document.getElementById("reviews-questions");

        if (!loading && element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        }
      };

      const handleLoadMoreQuestions = (event: { selected: number }) => {
        setQuestionPageCursor(event.selected + 1);

        const element = document.getElementById("questions-reviews");

        if (!loading && element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        }
      };

      const handleTabChange = (tab?: string) => {
        setReviewFilter(0);
        setPage(1);
        setQuestionPageCursor(1);
      };

      const handleSearchIconClick = () => {
        const searchInput = document.getElementById("search-keyword")?.value;
        setPage(1);
        setReviews([]);
        setSearchKeyword(searchInput);
      };

      const handleSearch = (e: KeyboardEventHandler<HTMLInputElement>) => {
        if (e.code === "Enter") {
          setPage(1);
          setReviews([]);
          setSearchKeyword(e.target.value);
        }
      };

      const handleReviewsSortOptionChange = ({
        target: { value },
      }: ChangeEvent<HTMLSelectElement>) => {
        setReviewsSortValue(value);
        setReviewFilter(0);
      };

      const handleQuestionsSortOptionChange = ({
        target: { value },
      }: ChangeEvent<HTMLSelectElement>) => {
        setQuestionsSortValue(value);
      };

      const handleClickReviewFilter = (filter: number) => {
        setPage(1);
        setReviews([]);
        setReviewFilter(filter);
      };

      const handleClearReviewFilter = () => {
        setPage(1);
        setReviews([]);
        setReviewFilter(0);
      };

      useEffect(() => {
        if (!sku) {
          return;
        }

        addProductToQueue(sku);
      }, [sku]);

      Component.displayName = name;

      return useMemo(
        () =>
          errors && errors.length > 0 ? null : (
            <Component
              additionalReviewsLoadingMoreText={
                additionalReviewsLoadingMoreText
              }
              averageScore={average}
              gender={gender}
              handleClearReviewFilter={handleClearReviewFilter}
              handleClickReviewFilter={handleClickReviewFilter}
              handleLoadMoreQuestions={handleLoadMoreQuestions}
              handleLoadMoreReviews={handleLoadMoreReviews}
              handleTabChange={handleTabChange}
              handleReviewsSortOptionChange={handleReviewsSortOptionChange}
              handleQuestionsSortOptionChange={handleQuestionsSortOptionChange}
              handleSearchIconClick={handleSearchIconClick}
              handleSearch={handleSearch}
              loading={loading}
              productGroup={productGroup}
              productUrl={productUrl}
              questions={questions}
              reviewFilter={reviewFilter}
              reviews={reviews}
              sku={sku}
              starDistribution={starDistribution}
              totalQuestions={totalQuestions}
              totalQuestionPageCount={totalQuestionPageCount}
              totalReviews={total}
              totalPageCount={totalPageCount}
              fitFeedbackStatistic={fitFeedbackStatistic}
              siblingColours={siblingColours}
              reviewsSummary={reviewsSummary}
              reviewsSortOptions={reviewsSortOptions}
              questionsSortOptions={questionsSortOptions}
              reviewsPerPage={reviewsPerPage}
              totalResultsCount={totalResultsCount}
              questionsPerPage={questionsPerPage}
            />
          ),
        [
          errors,
          gender,
          reviews,
          reviewFilter,
          average,
          questions,
          starDistribution,
          total,
          totalQuestions,
          totalPageCount,
          totalQuestionPageCount,
        ],
      );
    },
  );
